import { useState } from "react";
import axios from "axios";
import { Calendar, Clock, MapPin, Trophy, CircleCheck, ArrowRight, Flag } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, FadeIn, ParallaxImage } from "@/components/motion";
import { API, usePageMeta } from "@/lib/site";

const FACTS = [
  { icon: Calendar, label: "Date", value: "Saturday, August 29, 2026", testid: "golf-fact-date" },
  { icon: Clock, label: "Tee time", value: "7:00 AM – 2:00 PM MST", testid: "golf-fact-time" },
  { icon: MapPin, label: "Course", value: "NMSU Golf Course, Las Cruces, NM", testid: "golf-fact-venue" },
];

const TIERS = [
  {
    name: "Bronze",
    price: "$100",
    benefits: ["Your logo on the event banner", "Recognition on event day"],
    featured: false,
  },
  {
    name: "Silver",
    price: "$300",
    benefits: ["2 player registrations", "Your logo on the event banner"],
    featured: false,
  },
  {
    name: "Gold",
    price: "$500",
    benefits: [
      "4 player registrations",
      "Your logo on the event banner",
      "Recognition in the event program",
    ],
    featured: true,
  },
];

const INTERESTS = [
  "Play in the scramble",
  "Sponsor — Bronze $100",
  "Sponsor — Silver $300",
  "Sponsor — Gold $500",
  "Other",
];

const GolfScramble = () => {
  usePageMeta(
    "2026 HC Golf Scramble — Hope Centers International",
    "Tee off for the kids on August 29, 2026 at NMSU Golf Course, Las Cruces. Player spots and sponsor tiers from $100."
  );

  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: INTERESTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const valid = form.name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(form.email);

  const chooseTier = (tierName) => {
    const label = INTERESTS.find((i) => i.includes(tierName));
    if (label) setForm((f) => ({ ...f, interest: label }));
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    try {
      await axios.post(`${API}/event-interest`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("You're on the list — see you on the course.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="golf-scramble-page">
      {/* HERO */}
      <section className="pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn y={16}>
            <p className="label">22nd annual fundraiser</p>
          </FadeIn>
          <h1
            data-testid="golf-heading"
            className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98]"
          >
            <MaskedLines delay={0.15} lines={[<>Tee off</>, <><em className="text-terra">for the kids.</em></>]} />
          </h1>
          <FadeIn delay={0.5} className="mt-8 max-w-xl">
            <p className="text-base sm:text-lg leading-relaxed text-ink/75">
              The 2026 HC Golf Scramble is our biggest fundraiser of the year — one day on the course that stocks
              feeding centers and funds Dream Release scholarships for a whole season.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#register" data-testid="golf-register-button" className="btn-primary group">
                Register interest
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a href="#sponsors" data-testid="golf-sponsor-button" className="btn-outline">
                Become a sponsor
              </a>
            </div>
          </FadeIn>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {FACTS.map((f, i) => (
              <FadeIn key={f.label} delay={0.1 + i * 0.1}>
                <div data-testid={f.testid} className="flex items-start gap-4 border-t border-ink/15 pt-6">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-terra/10 text-terra shrink-0">
                    <f.icon size={18} />
                  </span>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-soft">{f.label}</p>
                    <p className="mt-1.5 font-semibold text-ink">{f.value}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 md:mt-20">
          <FadeIn delay={0.15}>
            <ParallaxImage
              src="/images/golf-1.jpg"
              alt="Golf carts lined up and ready at a past HC Golf Scramble"
              className="rounded-2xl aspect-[16/9] lg:aspect-[21/9]"
              speed={0.08}
            />
          </FadeIn>
        </div>
      </section>

      {/* WHY WE PLAY */}
      <section className="py-24 md:py-32 bg-forest text-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <FadeIn className="md:col-span-5">
            <span
              className="font-serif text-7xl md:text-8xl font-semibold leading-none"
              style={{ WebkitTextStroke: "1.5px #EC4849", color: "transparent" }}
            >
              22
            </span>
            <h2 className="mt-6 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Twenty-two years of fairways and full plates.
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="md:col-span-6 md:col-start-7">
            <p className="text-base sm:text-lg leading-relaxed text-paper/75">
              What started as a small charity round has grown into a Las Cruces tradition. Every player fee, mulligan,
              and sponsorship goes straight to the work — meals cooked daily at twenty-seven feeding centers and school
              costs covered for Dream Release students.
            </p>
            <p className="mt-5 text-base leading-relaxed text-paper/60">
              Four-player scramble format. Breakfast, lunch, and prizes included. All skill levels welcome — the
              scorecard matters far less than the cause.
            </p>
            <div className="mt-8 flex items-center gap-3 text-marigold">
              <Trophy size={20} />
              <p className="font-mono text-xs uppercase tracking-[0.25em]">Scramble format · All levels welcome</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PAST TOURNAMENTS GALLERY */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <p className="label">From past tournaments</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Twenty-two years of good days.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-6 md:gap-8">
            {[
              { src: "/images/gallery-1.jpg", alt: "Winners holding the tournament trophy at the awards banquet", cls: "md:col-span-7", ratio: "aspect-[4/3]" },
              { src: "/images/gallery-2.jpg", alt: "A basket of Hope Centers branded golf balls", cls: "md:col-span-5 md:mt-16", ratio: "aspect-[4/3]" },
              { src: "/images/gallery-3.jpg", alt: "Golfers on the fairway during a past scramble", cls: "md:col-span-5", ratio: "aspect-[4/3]" },
              { src: "/images/gallery-4.jpg", alt: "Players lining up putts on the green", cls: "md:col-span-7 md:mt-16", ratio: "aspect-[4/3]" },
              { src: "/images/gallery-5.jpg", alt: "A golf cart loaded with bags, ready for tee time", cls: "md:col-span-4", ratio: "aspect-[3/4]" },
              { src: "/images/gallery-6.jpg", alt: "Players and sponsors sharing lunch at the tournament banquet", cls: "md:col-span-8 md:mt-10", ratio: "aspect-[16/9]" },
            ].map((g, i) => (
              <FadeIn key={g.src} delay={(i % 3) * 0.08} className={g.cls}>
                <div className={`group overflow-hidden rounded-2xl ${g.ratio}`} data-testid={`golf-gallery-${i}`}>
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR TIERS */}
      <section id="sponsors" className="py-24 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-14">
            <p className="label">Sponsorship</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Put your name behind the cause.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {TIERS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`golf-tier-${t.name.toLowerCase()}`}
                  className={`h-full rounded-2xl p-8 md:p-10 flex flex-col border transition-[border-color,transform] duration-500 hover:-translate-y-1 ${
                    t.featured
                      ? "bg-forest text-paper border-forest"
                      : "bg-white text-ink border-ink/10 hover:border-terra/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`font-mono text-xs uppercase tracking-[0.25em] ${t.featured ? "text-paper/50" : "text-ink-soft"}`}>
                      {t.name}
                    </p>
                    {t.featured && (
                      <span className="rounded-full bg-terra px-3 py-1 text-[11px] font-bold text-paper">
                        Best value
                      </span>
                    )}
                  </div>
                  <p className={`mt-5 font-serif text-5xl font-medium tracking-tight ${t.featured ? "text-marigold" : "text-terra"}`}>
                    {t.price}
                  </p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.benefits.map((b) => (
                      <li key={b} className={`flex items-start gap-3 text-sm ${t.featured ? "text-paper/80" : "text-ink/75"}`}>
                        <CircleCheck size={17} className={`mt-0.5 shrink-0 ${t.featured ? "text-marigold" : "text-terra"}`} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    data-testid={`golf-sponsor-${t.name.toLowerCase()}-button`}
                    onClick={() => chooseTier(t.name)}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-[background-color,transform] duration-300 hover:scale-[1.03] ${
                      t.featured
                        ? "bg-terra text-paper hover:bg-terra-dark"
                        : "border border-ink/25 text-ink hover:bg-ink hover:text-paper"
                    }`}
                  >
                    Sponsor as {t.name}
                    <Flag size={15} />
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION INTEREST */}
      <section id="register" className="py-24 md:py-32 bg-paper-deep border-y border-ink/10 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <FadeIn className="lg:col-span-5">
            <p className="label">Registration interest</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Save your spot on the tee sheet.
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink/70">
              Tell us how you'd like to take part and we'll follow up with registration and payment details.
            </p>
            <div className="mt-10 bg-forest text-paper rounded-2xl p-8">
              <p className="font-serif text-2xl font-medium tracking-tight">Questions first?</p>
              <p className="mt-3 text-sm text-paper/70 leading-relaxed">
                Reach Sue directly at{" "}
                <a href="mailto:sue@hopecenters.org" className="text-marigold underline underline-offset-4">
                  sue@hopecenters.org
                </a>
                .
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div data-testid="golf-success" className="bg-white border border-ink/10 rounded-2xl p-10 md:p-14">
                <span className="grid place-items-center w-14 h-14 rounded-full bg-terra/10 text-terra">
                  <CircleCheck size={26} />
                </span>
                <h3 className="mt-8 font-serif text-4xl md:text-5xl font-medium tracking-tight">You're in the gallery.</h3>
                <p className="mt-4 text-ink/70 leading-relaxed max-w-md">
                  Thanks, {form.name.split(" ")[0]} — we've noted your interest in “{form.interest}” and will be in
                  touch at {form.email} with the next steps.
                </p>
                <button
                  type="button"
                  data-testid="golf-send-another-button"
                  onClick={() => {
                    setForm({ name: "", email: "", phone: "", interest: INTERESTS[0], message: "" });
                    setSent(false);
                  }}
                  className="btn-outline mt-8"
                >
                  Add another entry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="golf-interest-form"
                className="bg-white border border-ink/10 rounded-2xl p-8 md:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="golf-name" className="label block mb-1">Name</label>
                    <input
                      id="golf-name"
                      data-testid="golf-name-input"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="field"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="golf-email" className="label block mb-1">Email</label>
                    <input
                      id="golf-email"
                      data-testid="golf-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.org"
                      className="field"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="golf-phone" className="label block mb-1">
                      Phone <span className="normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="golf-phone"
                      data-testid="golf-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(575) 555-0100"
                      className="field"
                    />
                  </div>
                  <div>
                    <label htmlFor="golf-interest" className="label block mb-1">I want to</label>
                    <select
                      id="golf-interest"
                      data-testid="golf-interest-select"
                      value={form.interest}
                      onChange={(e) => setForm({ ...form, interest: e.target.value })}
                      className="field cursor-pointer"
                    >
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="golf-message" className="label block mb-1">
                    Notes <span className="normal-case tracking-normal">(teammates, handicap, questions)</span>
                  </label>
                  <textarea
                    id="golf-message"
                    data-testid="golf-message-input"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Anything we should know?"
                    className="field resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="golf-submit-button"
                  disabled={!valid || sending}
                  className="btn-primary group disabled:opacity-40 disabled:pointer-events-none"
                >
                  {sending ? "Sending…" : "Save my spot"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default GolfScramble;
